import {
  CommandsManager,
  ExtensionManager,
  ServicesManager,
  ServiceProvidersManager,
  HotkeysManager,
  UINotificationService,
  UIModalService,
  UIDialogService,
  UIViewportDialogService,
  MeasurementService,
  DisplaySetService,
  ToolbarService,
  ViewportGridService,
  HangingProtocolService,
  CineService,
  UserAuthenticationService,
  errorHandler,
  CustomizationService,
  PanelService,
  WorkflowStepsService,
  StudyPrefetcherService,
  MultiMonitorService,
  // utils,
} from '@ohif/core';

import loadModules, { loadModule as peerImport } from './pluginImports';
import { publicUrl } from './utils/publicUrl';
import { getDicomStorePathFromSearch, setDicomStoreInBrowserUrl } from './utils/dicomStoreUrl';

const GCP_HEALTHCARE_API_BASE = 'https://healthcare.googleapis.com/v1/';

function _getQueryParamCaseInsensitive(searchParams, key) {
  if (!searchParams) {
    return null;
  }

  if (searchParams.has(key)) {
    return searchParams.get(key);
  }

  const target = String(key || '').toLowerCase();
  for (const [k, v] of searchParams.entries()) {
    if (String(k).toLowerCase() === target) {
      return v;
    }
  }

  return null;
}

function _tryGetGcpStorePathFromDicomwebRoot(dicomwebRoot) {
  const raw = String(dicomwebRoot || '').trim();
  if (!raw) {
    return null;
  }

  // Accept both:
  // - https://healthcare.googleapis.com/v1/projects/.../dicomStores/<store>/dicomWeb
  // - projects/.../dicomStores/<store>/dicomWeb
  const match = raw.match(
    /projects\/[^/]+\/locations\/[^/]+\/datasets\/[^/]+\/dicomStores\/[^/?#]+/i
  );
  return match?.[0] || null;
}

function _hasStudyInstanceUIDsInUrl(searchParams) {
  return [...searchParams.keys()].some(key => String(key).toLowerCase() === 'studyinstanceuids');
}

function _buildGcpDicomwebRootFromStorePath(storePath) {
  const trimmed = String(storePath || '').trim();
  if (!trimmed) {
    return undefined;
  }

  // Normalize:
  // - allow either "projects/..." or "/projects/..."
  // - allow a full URL
  const withoutLeadingSlash = trimmed.replace(/^\//, '');
  if (/^https?:\/\//i.test(withoutLeadingSlash)) {
    return withoutLeadingSlash.replace(/\/+$/, '');
  }

  const base = GCP_HEALTHCARE_API_BASE.replace(/\/+$/, '/');
  const path = withoutLeadingSlash.replace(/^v1\//, '');

  // Allow passing either a DICOM store resource path, or a dicomWeb root path
  // (e.g. ".../dicomStores/X" vs ".../dicomStores/X/dicomWeb").
  if (path.toLowerCase().endsWith('/dicomweb')) {
    return `${base}${path}`;
  }

  return `${base}${path}/dicomWeb`;
}

/**
 * @param {object|func} appConfigOrFunc - application configuration, or a function that returns application configuration
 * @param {object[]} defaultExtensions - array of extension objects
 */
async function appInit(appConfigOrFunc, defaultExtensions, defaultModes) {
  const commandsManagerConfig = {
    getAppState: () => {},
  };

  const commandsManager = new CommandsManager(commandsManagerConfig);
  const servicesManager = new ServicesManager(commandsManager);
  const serviceProvidersManager = new ServiceProvidersManager();
  const hotkeysManager = new HotkeysManager(commandsManager, servicesManager);

  const appConfig = {
    ...(typeof appConfigOrFunc === 'function'
      ? await appConfigOrFunc({ servicesManager, peerImport })
      : appConfigOrFunc),
  };
  // Default the peer import function
  appConfig.peerImport ||= peerImport;
  appConfig.measurementTrackingMode ||= 'standard';
  appConfig.routerBasename ||= publicUrl;

  const extensionManager = new ExtensionManager({
    commandsManager,
    servicesManager,
    serviceProvidersManager,
    hotkeysManager,
    appConfig,
  });

  servicesManager.setExtensionManager(extensionManager);

  servicesManager.registerServices([
    [MultiMonitorService.REGISTRATION, appConfig.multimonitor],
    UINotificationService.REGISTRATION,
    UIModalService.REGISTRATION,
    UIDialogService.REGISTRATION,
    UIViewportDialogService.REGISTRATION,
    MeasurementService.REGISTRATION,
    DisplaySetService.REGISTRATION,
    [CustomizationService.REGISTRATION, appConfig.customizationService],
    ToolbarService.REGISTRATION,
    ViewportGridService.REGISTRATION,
    HangingProtocolService.REGISTRATION,
    CineService.REGISTRATION,
    UserAuthenticationService.REGISTRATION,
    PanelService.REGISTRATION,
    WorkflowStepsService.REGISTRATION,
    [StudyPrefetcherService.REGISTRATION, appConfig.studyPrefetcher],
  ]);

  errorHandler.getHTTPErrorHandler = () => {
    if (typeof appConfig.httpErrorHandler === 'function') {
      return appConfig.httpErrorHandler;
    }
  };

  /**
   * Example: [ext1, ext2, ext3]
   * Example2: [[ext1, config], ext2, [ext3, config]]
   */
  const loadedExtensions = await loadModules([...defaultExtensions, ...appConfig.extensions]);
  await extensionManager.registerExtensions(loadedExtensions, appConfig.dataSources);

  // Restore DICOM store selection from localStorage so refresh keeps the user's choice
  const defaultDataSourceName = appConfig.defaultDataSourceName || 'dicomweb';
  const defaultDataSourceDef = extensionManager.getDataSourceDefinition(defaultDataSourceName);
  const persistDicomStoreSelection =
    defaultDataSourceDef?.configuration?.persistDicomStoreSelection !== false;
  const buildDefaultDicomwebRoot =
    defaultDataSourceDef?.configuration?.qidoRoot ||
    defaultDataSourceDef?.configuration?.wadoRoot ||
    defaultDataSourceDef?.configuration?.wadoUriRoot;

  const storageKey = `ohif.dataSource.${defaultDataSourceName}.config`;

  if (!persistDicomStoreSelection) {
    // Always start from the build-time default (.env / google.js), not a prior session.
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(storageKey);
      }
    } catch (e) {
      // ignore
    }
  } else {
    // Restore DICOM store selection from localStorage so refresh keeps the user's choice
    try {
      const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(storageKey) : null;
      if (saved) {
        const parsed = JSON.parse(saved);
        const def = extensionManager.getDataSourceDefinition(defaultDataSourceName);
        if (def && parsed.wadoUriRoot && parsed.qidoRoot && parsed.wadoRoot) {
          const merged = { ...def.configuration, ...parsed };
          extensionManager.updateDataSourceConfiguration(defaultDataSourceName, merged);
        }
      }
    } catch (e) {
      // Ignore invalid or missing stored config
    }
  }

  // URL override for selecting a specific Google Cloud Healthcare DICOM store / DICOMweb root.
  // Shareable links use the compact tilde format; legacy dicomStore=... links still work:
  // - /viewer?StudyInstanceUIDs=...&dicomStore~projects~...~locations~...~datasets~...~dicomStores~YourStore
  // Legacy links with dicomwebRoot alone are still supported.
  //
  // URL override wins over localStorage so shared links always open the intended store.
  try {
    const sp = new URLSearchParams(window?.location?.search || '');
    const dicomwebRootFromUrl =
      _getQueryParamCaseInsensitive(sp, 'dicomwebRoot') ||
      _getQueryParamCaseInsensitive(sp, 'dicomWebRoot');

    const dicomStorePathFromUrl = getDicomStorePathFromSearch(window?.location?.search || '', sp);

    const dicomwebRootFromPersistence =
      (dicomwebRootFromUrl && String(dicomwebRootFromUrl).trim()) ||
      _buildGcpDicomwebRootFromStorePath(dicomStorePathFromUrl);
    const hasStoreInUrl = !!dicomStorePathFromUrl;
    const hasRootInUrl = !!dicomwebRootFromUrl;
    const hasExplicitStoreInUrl = hasStoreInUrl || hasRootInUrl;

    const applyDicomwebRoot = (dicomwebRoot, shouldPersistToLocalStorage = persistDicomStoreSelection) => {
      if (!dicomwebRoot) {
        return;
      }

      const def = extensionManager.getDataSourceDefinition(defaultDataSourceName);
      if (!def) {
        return;
      }

      const merged = {
        ...def.configuration,
        wadoUriRoot: dicomwebRoot,
        qidoRoot: dicomwebRoot,
        wadoRoot: dicomwebRoot,
      };

      extensionManager.updateDataSourceConfiguration(defaultDataSourceName, merged);

      if (shouldPersistToLocalStorage) {
        try {
          localStorage.setItem(
            storageKey,
            JSON.stringify({
              wadoUriRoot: dicomwebRoot,
              qidoRoot: dicomwebRoot,
              wadoRoot: dicomwebRoot,
            })
          );
        } catch (e) {
          // ignore persistence failures
        }
      }
    };

    const normalizeDicomStoreUrlParam = dicomwebRoot => {
      const urlStorePath = _tryGetGcpStorePathFromDicomwebRoot(dicomwebRoot);
      if (urlStorePath) {
        setDicomStoreInBrowserUrl(urlStorePath);
      }
    };

    if (hasExplicitStoreInUrl && dicomwebRootFromPersistence) {
      // Shared links always honor dicomStore / dicomwebRoot in the URL, even when
      // persistDicomStoreSelection is false (build default is used only without URL params).
      applyDicomwebRoot(dicomwebRootFromPersistence);
      normalizeDicomStoreUrlParam(dicomwebRootFromPersistence);
    } else if (!persistDicomStoreSelection) {
      // No store in URL: align the address bar with the build-time default (.env),
      // unless this is a direct study link — the viewer resolves the correct store first.
      const buildStorePath = _tryGetGcpStorePathFromDicomwebRoot(buildDefaultDicomwebRoot);
      if (buildStorePath && !_hasStudyInstanceUIDsInUrl(sp)) {
        setDicomStoreInBrowserUrl(buildStorePath);
      }
    } else {
      const def = extensionManager.getDataSourceDefinition(defaultDataSourceName);
      const effectiveRoot =
        def?.configuration?.qidoRoot ||
        def?.configuration?.wadoRoot ||
        def?.configuration?.wadoUriRoot;
      const storePath = _tryGetGcpStorePathFromDicomwebRoot(effectiveRoot);

      if (!hasExplicitStoreInUrl && effectiveRoot && storePath) {
        // If the store/root are known from config/localStorage but not present in the URL,
        // make the landing URL shareable by injecting the params without navigating.
        normalizeDicomStoreUrlParam(effectiveRoot);
      }
    }
  } catch (e) {
    // Ignore invalid or missing stored config
  }

  // TODO: We no longer use `utils.addServer`
  // TODO: We no longer init webWorkers at app level
  // TODO: We no longer init the user Manager

  if (!appConfig.modes) {
    throw new Error('No modes are defined! Check your app-config.js');
  }

  const loadedModes = await loadModules([...(appConfig.modes || []), ...defaultModes]);

  // This is the name for the loaded instance object
  appConfig.loadedModes = [];
  const modesById = new Set();
  for (let i = 0; i < loadedModes.length; i++) {
    let mode = loadedModes[i];
    if (!mode) {
      continue;
    }
    const { id } = mode;

    if (mode.modeFactory) {
      // If the appConfig contains configuration for this mode, use it.
      const modeConfiguration =
        appConfig.modesConfiguration && appConfig.modesConfiguration[id]
          ? appConfig.modesConfiguration[id]
          : {};

      mode = await mode.modeFactory({ modeConfiguration, loadModules });
    }

    if (modesById.has(id)) {
      continue;
    }
    // Prevent duplication
    modesById.add(id);
    if (!mode || typeof mode !== 'object') {
      continue;
    }
    appConfig.loadedModes.push(mode);
  }
  // Hack alert - don't touch the original modes definition,
  // but there are still dependencies on having the appConfig modes defined
  appConfig.modes = appConfig.loadedModes;

  return {
    appConfig,
    commandsManager,
    extensionManager,
    servicesManager,
    serviceProvidersManager,
    hotkeysManager,
  };
}

export default appInit;
